import { useState } from "react";
import {
  tileCsvUrl,
  tileHtmlUrl,
  useDeleteTile,
  useRefreshTile,
  useSwapTileKind,
  useUpdateTile,
  type Tile,
} from "@/api/queries";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Menu as DropdownMenu } from "@/components/ui/Menu";
import {
  AlertIcon,
  ChartIcon,
  CodeIcon,
  DatabaseIcon,
  DownloadIcon,
  GripIcon,
  MoreIcon,
  RefreshIcon,
  TrashIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { TileChartDialog } from "./TileChartDialog";
import { useRelativeTime } from "@/lib/time";

/**
 * One tile in a grid (PRD FR-7.2).
 *
 * The artifact inside is rendered by the same `ArtifactCard` the chat
 * workspace uses — a tile is that card in a grid frame, not a second renderer.
 * What this adds is everything a tile owns and an answer does not: the frame,
 * the connection badge, the freshness stamp, and the menu (refresh, width and
 * height, Edit SQL, view SQL, remove).
 *
 * Freshness is stated rather than implied. A tile that has never been
 * refreshed says it is showing the values its answer produced; one whose
 * refresh failed shows the previous values with the failure beside them, which
 * is the difference between a stale number and an empty grid (PRD FR-7.4).
 */

type TileStatus = { tone: "success" | "warning" | "error" | "neutral"; label: string };

const FALLBACK_STATUS: TileStatus = { tone: "neutral", label: "as answered" };

const STATUS: Record<string, TileStatus> =
  {
    ok: { tone: "success", label: "refreshed" },
    saved: { tone: "neutral", label: "as answered" },
    static: { tone: "neutral", label: "no query" },
    blocked: { tone: "error", label: "blocked" },
    error: { tone: "error", label: "refresh failed" },
  };

function Menu({
  tile,
  onEditSql,
  onEditChart,
  onViewSql,
  onRemove,
  onResize,
  onRefresh,
  busy,
}: {
  tile: Tile;
  onEditSql: () => void;
  onEditChart: () => void;
  onViewSql: () => void;
  onRemove: () => void;
  onResize: (frame: { col_span?: number; row_span?: number }) => void;
  onRefresh: () => void;
  busy: boolean;
}) {
  const item =
    "type-support text-ink hover:bg-hover rounded-inner motion-fast flex w-full items-center gap-2 px-2 py-1.5 text-start";

  return (
    <DropdownMenu
      label="Tile menu"
      className="w-56"
      trigger={(props) => (
        <button
          type="button"
          aria-label="Tile menu"
          {...props}
          className="text-ink-secondary hover:text-ink hover:bg-hover rounded-inner motion-fast inline-flex size-6 items-center justify-center"
        >
          <MoreIcon className="size-4" />
        </button>
      )}
    >
      {(close) => (
        <>
          <button
            type="button"
            role="menuitem"
            className={item}
            disabled={!tile.query_backed || busy}
            onClick={() => {
              close();
              onRefresh();
            }}
          >
            <RefreshIcon className="size-3.5" />
            Refresh
          </button>

          <div className="border-border my-1 border-t" />
          <p className="type-caption text-ink-disabled px-2 py-1">Width</p>
          <div className="flex gap-1 px-1 pb-1">
            {[1, 2, 3, 4].map((span) => (
              <button
                key={span}
                type="button"
                role="menuitemradio"
                aria-checked={tile.col_span === span}
                onClick={() => onResize({ col_span: span })}
                className={cn(
                  "type-support rounded-inner motion-fast flex-1 py-1",
                  tile.col_span === span
                    ? "bg-selected text-ink"
                    : "text-ink-secondary hover:bg-hover",
                )}
              >
                {span}
              </button>
            ))}
          </div>
          <p className="type-caption text-ink-disabled px-2 py-1">Height</p>
          <div className="flex gap-1 px-1 pb-1">
            {[1, 2, 3].map((span) => (
              <button
                key={span}
                type="button"
                role="menuitemradio"
                aria-checked={tile.row_span === span}
                onClick={() => onResize({ row_span: span })}
                className={cn(
                  "type-support rounded-inner motion-fast flex-1 py-1",
                  tile.row_span === span
                    ? "bg-selected text-ink"
                    : "text-ink-secondary hover:bg-hover",
                )}
              >
                {span}
              </button>
            ))}
          </div>

          <div className="border-border my-1 border-t" />
          {tile.columns?.length ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => {
                close();
                onEditChart();
              }}
            >
              <ChartIcon className="size-3.5" />
              Edit chart
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              close();
              onEditSql();
            }}
          >
            <CodeIcon className="size-3.5" />
            {tile.query_backed ? "Edit SQL" : "Add SQL"}
          </button>
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              close();
              onViewSql();
            }}
          >
            <CodeIcon className="size-3.5" />
            View SQL
          </button>
          {/* A plain link: the endpoint answers with an attachment (HTML-1). */}
          <a
            role="menuitem"
            className={item}
            href={tileHtmlUrl(tile.id)}
            onClick={close}
          >
            <DownloadIcon className="size-3.5" />
            Download as HTML
          </a>
          <div className="border-border my-1 border-t" />
          <button
            type="button"
            role="menuitem"
            className={cn(item, "text-error")}
            onClick={() => {
              close();
              onRemove();
            }}
          >
            <TrashIcon className="size-3.5" />
            Remove
          </button>
        </>
      )}
    </DropdownMenu>
  );
}

export function TileCard({
  tile,
  linkIndex,
  showConnection,
  onEditSql,
  onViewSql,
  onGrab,
  onMove,
  dragging,
  position,
  total,
}: {
  tile: Tile;
  /** The tile's position in its grid's links (LINK-7). */
  linkIndex?: number;
  /** Dashboards badge every tile; a report has one source in its header. */
  showConnection: boolean;
  onEditSql: () => void;
  onViewSql: () => void;
  /** Pointer drag, from the grid. */
  onGrab: (event: React.DragEvent) => void;
  /** Keyboard reorder, so the grid is usable without a mouse (PRD NFR-5). */
  onMove: (delta: number) => void;
  dragging: boolean;
  position: number;
  total: number;
}) {
  const refresh = useRefreshTile();
  const swap = useSwapTileKind();
  const update = useUpdateTile();
  const remove = useDeleteTile({ kind: tile.owner_kind, id: tile.owner_id });
  const [confirming, setConfirming] = useState(false);
  const [charting, setCharting] = useState(false);
  const stamp = useRelativeTime(tile.refreshed_at);
  const status = STATUS[tile.refresh_status] ?? FALLBACK_STATUS;
  const failed = tile.refresh_status === "error" || tile.refresh_status === "blocked";

  return (
    <>
      <ArtifactCard
        artifact={tile.artifact}
        linkIndex={linkIndex}
        csvUrl={tileCsvUrl(tile.id)}
        swapping={swap.isPending}
        onSwap={(type) => swap.mutateAsync({ tileId: tile.id, type })}
        onRename={(title) => update.mutateAsync({ tileId: tile.id, title })}
        renameLabel="tile"
        className={cn(
          "motion-fast h-full",
          dragging ? "shadow-dragging border-accent opacity-90" : "shadow-card",
        )}
        fillChart
        compact
        bodyClassName="overflow-auto"
        actions={
          <div className="flex items-center gap-1">
            {refresh.isPending ? (
              <RefreshIcon className="text-ink-disabled size-3.5 animate-spin" />
            ) : null}
            <button
              type="button"
              draggable
              onDragStart={onGrab}
              aria-label={`Move ${tile.title || "tile"}, position ${position} of ${total}`}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                  event.preventDefault();
                  onMove(-1);
                }
                if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                  event.preventDefault();
                  onMove(1);
                }
              }}
              className="text-ink-disabled hover:text-ink hover:bg-hover rounded-inner motion-fast inline-flex size-6 cursor-grab items-center justify-center active:cursor-grabbing"
            >
              <GripIcon className="size-4" />
            </button>
            <Menu
              tile={tile}
              busy={refresh.isPending}
              onRefresh={() => refresh.mutate(tile.id)}
              onEditSql={onEditSql}
              onEditChart={() => setCharting(true)}
              onViewSql={onViewSql}
              onRemove={() => setConfirming(true)}
              onResize={(frame) => update.mutate({ tileId: tile.id, ...frame })}
            />
          </div>
        }
        subtitle={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {showConnection && tile.connection_name ? (
              <span className="type-caption text-ink-secondary inline-flex items-center gap-1">
                <DatabaseIcon className="size-3" />
                {tile.connection_name}
              </span>
            ) : null}
            <span className="type-caption text-ink-disabled inline-flex items-center gap-1.5">
              <StatusDot tone={status.tone} />
              {status.label}
              {stamp ? ` · ${stamp}` : ""}
            </span>
            {failed ? (
              <span className="type-caption text-error inline-flex items-center gap-1 normal-case">
                <AlertIcon className="size-3" />
                {tile.refresh_error ?? "This tile could not be refreshed."}
              </span>
            ) : null}
            {!tile.query_backed ? <Chip tone="neutral">static</Chip> : null}
          </div>
        }
      />
      {/* A tile is the one thing here whose removal is *not* obviously
          recoverable: its spec, its statement and the rows it last returned were
          copied at capture, so nothing upstream can give them back. The answer
          it came from may not even exist any more. */}
      <TileChartDialog tile={tile} open={charting} onClose={() => setCharting(false)} />
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        loading={remove.isPending}
        title="Remove this tile?"
        confirmLabel="Remove"
        description={
          <>
            “{tile.title || tile.artifact.title || "Untitled tile"}” will be removed from this{" "}
            {tile.owner_kind}.
          </>
        }
        consequence={
          // Three cases, because two of the three sentences are lies in the
          // others: a typed tile was copied from no answer, and a static tile
          // has no statement to lose.
          !tile.query_backed
            ? "This tile has no query, so its values exist nowhere else."
            : tile.source_message_id
              ? "Its statement and the values it last returned go with it. The answer it was copied from is not touched."
              : "Its statement and the values it last returned go with it, and this is the only copy — it was typed here rather than captured from an answer."
        }
        onConfirm={() =>
          remove.mutate(tile.id, { onSuccess: () => setConfirming(false) })
        }
      />
    </>
  );
}
